import { useEffect, useRef, useState } from "react";
import clamp from "lodash-es/clamp";

/**
 * Synthetic front‑loaded progress.
 * - eases toward 99% by ~120s
 */
export function useSyntheticProgress(isActive: boolean): number {
  const startRef = useRef<number | null>(null);
  const prevRef = useRef(0);
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    if (!isActive) {
      startRef.current = null;
      prevRef.current = 0;
      setPercent(0);
      return;
    }

    if (!startRef.current) startRef.current = performance.now();
    let frameId: number;

    const tick = () => {
      const now = performance.now();
      const elapsed = now - (startRef.current ?? now);
      const fastPhaseMs = 6_000;
      const totalMs = 120_000;
      const clampedElapsed = clamp(elapsed, 0, totalMs);

      const fastPortion = clamp(clampedElapsed / fastPhaseMs, 0, 1);
      const slowPortion = clamp(
        clampedElapsed <= fastPhaseMs
          ? 0
          : (clampedElapsed - fastPhaseMs) / Math.max(1, totalMs - fastPhaseMs),
        0,
        1
      );

      const base = 80 * Math.sqrt(fastPortion) + 19 * Math.pow(slowPortion, 0.8);

      const t = now / 1000;
      const jitter = (Math.sin(t * 2.1) + Math.sin(t * 0.6 + 1.7) + Math.sin(t * 3.3 + 0.4)) * 1.2; // ~±3.6

      const withJitter = clamp(base + jitter, 0, 99);
      const next = Math.min(99, Math.max(prevRef.current, withJitter));
      prevRef.current = next;
      setPercent(next);
      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [isActive]);

  return percent;
}
